import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { VehicleDto, VehicleSpecialBondRequestDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import BondSpecialRequestAddEditDialog from './BondSpecialRequestAddEditDialog'

interface BondSpecialRequestCardProps {
  vehicle: VehicleDto
}

export default function BondSpecialRequestCard({ vehicle }: BondSpecialRequestCardProps) {
  const [isGettingBondSpecialRequest, setIsGettingBondSpecialRequest] = useState<boolean>(false)
  const [BondSpecialRequestAddEditDialogOpen, setBondSpecialRequestAddEditDialogOpen] = useState<boolean>(false)
  const [isSavingBondSpecialRequest, setIsSavingBondSpecialRequest] = useState<boolean>(false)
  const [bondSpecialRequest, setBondSpecialRequest] = useState<VehicleSpecialBondRequestDto>(new VehicleSpecialBondRequestDto())

  useEffect(() => {
    if (vehicle.vehicleGuid) {
      setIsGettingBondSpecialRequest(true)
      controllers
        .getSpecialBondRequest(vehicle.vehicleGuid!)
        .then(response => {
          setBondSpecialRequest(response.value)
        })
        .finally(() => {
          setIsGettingBondSpecialRequest(false)
        })
    }
  }, [vehicle])

  return (
    <>
      <BondSpecialRequestAddEditDialog
        disabled={Boolean(vehicle.vehicleGuid && vehicle.isLocked)}
        initialValues={bondSpecialRequest}
        isLoading={isSavingBondSpecialRequest}
        onClose={() => {
          setBondSpecialRequestAddEditDialogOpen(false)
        }}
        onSave={(values: VehicleSpecialBondRequestDto) => {
          setIsSavingBondSpecialRequest(true)
          controllers
            .updateSpecialBondRequest(vehicle.vehicleGuid!, values)
            .then(response => {
              setBondSpecialRequest(response.value)
              setBondSpecialRequestAddEditDialogOpen(false)
              enqueueSnackbar('Bond Special Request Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setIsSavingBondSpecialRequest(false)
            })
        }}
        open={BondSpecialRequestAddEditDialogOpen}
      />

      <Card variant='outlined'>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Special Bond Request
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    setBondSpecialRequestAddEditDialogOpen(true)
                  }}
                  variant='text'
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingBondSpecialRequest && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingBondSpecialRequest && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Insurance Company' value={bondSpecialRequest.insuranceCompany?.name} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Insurance Company Email'
                    value={bondSpecialRequest.insuranceCompanyEmail}
                    to={'mailto:' + bondSpecialRequest.insuranceCompanyEmail}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Special Notes' value={bondSpecialRequest.specialNotes} />
                </Grid>
              </>
            )}
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <ExplanationAccordion variant='outlined'>
              Pressing “Edit” opens the Send Special Bond Request dialog. Whatever information is entered or selected in that
              dialog will be displayed on this card for reference.
            </ExplanationAccordion>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
